import { useCallback, useEffect, useState } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

export const useAdManagerControl = (
  adsManager?: google.ima.AdsManager,
  youtubePlayer?: YouTubePlayer,
) => {
  const [isMute, setIsMute] = useState(true);

  const handleOnVolumeChange = useCallback(() => {
    setIsMute(false);
    void youtubePlayer?.unMute();
  }, [youtubePlayer]);

  const handleOnMute = useCallback(() => {
    setIsMute(true);
    void youtubePlayer?.mute();
  }, [youtubePlayer]);

  const handleChangeVolume = useCallback(
    (mute: boolean) => {
      adsManager?.setVolume(mute ? 0 : 1);
      setIsMute(mute);
    },
    [adsManager],
  );

  const handleOnAdStarted = useCallback(async () => {
    const isMuted = (await youtubePlayer?.isMuted()) ?? false;
    handleChangeVolume(isMuted);
  }, [handleChangeVolume, youtubePlayer]);

  useEffect(() => {
    if (!adsManager) return;
    adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
    adsManager.addEventListener(google.ima.AdEvent.Type.VOLUME_CHANGED, handleOnVolumeChange);
    adsManager.addEventListener(google.ima.AdEvent.Type.VOLUME_MUTED, handleOnMute);

    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
      adsManager.removeEventListener(google.ima.AdEvent.Type.VOLUME_CHANGED, handleOnVolumeChange);
      adsManager.removeEventListener(google.ima.AdEvent.Type.VOLUME_MUTED, handleOnMute);
    };
  }, [adsManager, handleOnAdStarted, handleOnMute, handleOnVolumeChange]);

  return {
    handleChangeVolume,
    isMute,
  };
};
